import { useMemo } from 'react';

import {
	fromEnumName,
	getFieldEntries,
	type HcoFieldKey,
	type HcpFieldKey,
	toEnumNames
} from '@/core/dcr/fields/registry';
import type { CommonFieldDef } from '@/core/dcr/fields/types';
import { EntityType } from '@/core/graphql/generated/types';

import { useDcrFormContext } from '../_context/DcrFormContext';
import { useGetSuggestedDcrFieldsQuery } from '../_graphql/query/GetSuggestedDcrFields.hook';

import { isSuggestible } from './useAddableFields';

const MAX_SUGGESTED_FIELDS = 3;
const SUGGESTION_QUERY_LIMIT = 20;

const FALLBACK_SUGGESTIONS: {
	[EntityType.Hcp]: readonly HcpFieldKey[];
	[EntityType.Hco]: readonly HcoFieldKey[];
} = {
	[EntityType.Hcp]: [
		'nhidCda_V',
		'gender_VId',
		'licenses_V',
		'birthYear_V',
		'emailCda_V'
	],
	[EntityType.Hco]: [
		'websiteCda_V',
		'nhidCda_V',
		'emailCda_V',
		'hcoTypeLocal_VId'
	]
};

export const getSuggestionCandidates = (
	entityType: EntityType,
	isAvailable: (id: string, def: CommonFieldDef) => boolean
) =>
	new Set(
		getFieldEntries(entityType)
			.filter(
				entry => isSuggestible(entry) && isAvailable(entry.key, entry.def)
			)
			.map(({ key }) => key)
	);

export const buildSuggestionInput = (
	entityType: EntityType,
	candidates: ReadonlySet<string>
) => ({
	entityType,
	fieldsToIgnore: toEnumNames(
		entityType,
		getFieldEntries(entityType)
			.filter(({ key }) => !candidates.has(key))
			.map(({ key }) => key)
	),
	limit: SUGGESTION_QUERY_LIMIT
});

export const useSuggestedFields = () => {
	const { entityType, isAvailable, countryId } = useDcrFormContext();

	const candidates = useMemo(
		() => getSuggestionCandidates(entityType, isAvailable),
		[entityType, isAvailable]
	);

	const input = useMemo(
		() => buildSuggestionInput(entityType, candidates),
		[candidates, entityType]
	);

	const { data, loading } = useGetSuggestedDcrFieldsQuery({
		skip: !countryId,
		variables: { input }
	});

	return useMemo(() => {
		if (loading) return [];

		const ranked =
			data?.suggestedDcrFields.__typename === 'SuggestedDCRFields'
				? data.suggestedDcrFields.results.flatMap(name => {
						const key = fromEnumName(entityType, name);
						return key ? [key] : [];
					})
				: [];

		return Array.from(new Set([...ranked, ...FALLBACK_SUGGESTIONS[entityType]]))
			.filter(key => candidates.has(key))
			.slice(0, MAX_SUGGESTED_FIELDS);
	}, [candidates, data, entityType, loading]);
};
